import React from 'react';
import { Button, useToasts } from 'freemium-ui';
import { History } from 'history';
import { isNaN, isNil } from 'lodash';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import {
  CONTACTS_IMPORT_LIMIT,
  CONTACT_LISTS_LIMIT,
  SEGMENTS_LIMIT,
} from 'routes/constants';
import { useAccessControl } from 'utils/access-control';
import { McrContactType } from '../../../shared/types';
import { BulkUpdateType, HeaderComponentProps } from './types';
import {
  useBulkDeleteContacts,
  useBulkUpdateContacts,
} from '../../screens/contacts/hooks';
import CreateNewListButton from '../CreateNewListButton';
import EditColumnsButton from '../EditColumnsButton';
import ExportColumnsButton from '../ExportColumnsButton';
import NewButton from '../NewButton';
import RemoveFromListButton from '../RemoveFromListButton';
import SearchInput from '../SearchInput';
import UpdateFieldsButton from '../UpdateFieldsButton';

// const blockFilter = (row: McrContactType) => !row?.fields?.is_blocked;
// const unBlockFilter = (row: McrContactType) => row?.fields?.is_blocked;
const deactivateFilter = (row: McrContactType) =>
  row?.fields?.contact_status === 'ACTIVE';
const activateFilter = (row: McrContactType) =>
  row?.fields?.contact_status === 'IN_ACTIVE';
const unSubscribeFilter = (row: McrContactType) =>
  !row?.fields?.is_unsubscribed;
const subscribeFilter = (row: McrContactType) => row?.fields?.is_unsubscribed;

const HeaderComponent = (props: HeaderComponentProps) => {
  const { t } = useTranslation();
  const {
    selectedRows = [],
    contactLists,
    segmentsCount,
    isLoading,
    contactListsCount,
    filterViewComponent,
    filterViewCount,
    showSearchInActions = false,
    fields,
    columns,
    defaultFilterCondition,
    filter,
    type,
    rule,
    setSelectedRowIds,
    hideLists,
    isAllSelected,
    executeSegment,
    contactList,
    setConfirmationData,
    setColumnOrder,
    hideNewButton = true,
    hideSearch = false,
    hideEditColumns = false,
    hideExportColumns = false,
    showNewContact,
    showNewList,
    showNewSegemnt,
    columnSettings,
    setHiddenColumns,
    columnOrder,
    hiddenColumns,
    accountInfo,
    userInfo,
    contactsCount,
  } = props;

  const [, setToast] = useToasts();
  const history: History = useHistory();
  const { access = [] } = useAccessControl();
  const { mutate: bulkUpdateContacts, isLoading: isLoadingBulkpdateContacts } =
    useBulkUpdateContacts({
      callback: (
        isSuccess: boolean,
        response: boolean,
        { callback: triggerCallback },
      ) => {
        if (isSuccess && response) {
          if (setSelectedRowIds) setSelectedRowIds({});
          if (executeSegment) executeSegment();
          setConfirmationData({});
          setToast({
            type: 'info',
            delay: 10000,
            showProgress: true,
            fixed: true,
            text: `${t('forms.toast.success.bulk_field_update')}`,
          });
        } else {
          setConfirmationData((old) => ({
            ...old,
            confirmButtonDisabled: false,
          }));
          setToast({
            type: 'error',
            delay: 3000,
            text: `${t('forms.toast.error.bulk_field_update')}`,
          });
        }
        if (triggerCallback) triggerCallback(isSuccess, response);
      },
    });

  const { mutate: bulkDeleteContacts } = useBulkDeleteContacts({
    setToast,
    callback: (isSuccess) => {
      if (isSuccess) {
        setConfirmationData({});
        // To-Do - If the header component unmounts before the executte segment
        // because of the selected contacts being empty use a settimeout function
        setSelectedRowIds?.({});
        executeSegment?.();
      } else {
        setConfirmationData((old) => ({
          ...old,
          confirmButtonDisabled: false,
        }));
      }
    },
  });

  const deleteSelectedContacts = () => {
    bulkDeleteContacts(
      (selectedRows || [])
        .map((selectedRow) => selectedRow.id || '')
        .filter((val) => !!val),
    );
  };
  const bulkUpdateSelectedContacts = async ({
    callback,
    fields,
    type = 'UPDATE',
  }: BulkUpdateType) => {
    const contact_ids = (selectedRows || [])
      .map((contact) => contact.id || '')
      .filter((val) => !!val);
    bulkUpdateContacts({
      fields: fields || {},
      type,
      contact_ids:
        isAllSelected || !contact_ids || !contact_ids.length
          ? null
          : contact_ids,
      rule: isAllSelected ? rule : null,
      callback,
    });
  };

  // const blockCount = (selectedRows || []).filter(blockFilter).length;
  // const unBlockCount = (selectedRows || []).filter(unBlockFilter).length;
  const deactivateCount = (selectedRows || []).filter(deactivateFilter).length;
  const activateCount = (selectedRows || []).filter(activateFilter).length;
  const unSubscribeCount = (selectedRows || []).filter(
    unSubscribeFilter,
  ).length;
  const subscribeCount = (selectedRows || []).filter(subscribeFilter).length;
  return (
    <ComponentContainer>
      {(selectedRows || []).length > 0 &&
      access.indexOf('contact:write') > -1 ? (
        <>
          <div className="table-action-buttons">
            {!isAllSelected && (
              <Button
                type="secondary"
                inline
                onClick={() =>
                  setConfirmationData({
                    id: new Date().toISOString(),
                    title:
                      (selectedRows || []).length === 1
                        ? `${t('contacts.menu.delete.title')}`
                        : `${t('contacts.menu.delete.title_multi')}`,
                    onConfirm: () => {
                      setConfirmationData((old) => ({
                        ...old,
                        confirmButtonDisabled: true,
                      }));
                      deleteSelectedContacts();
                    },
                    confirmButtonText: `${t('delete')}`,
                    className: 'error',
                    fill: '#d7461f',
                    infoText: `${t('contacts.menu.delete.infotext')}`,
                    description: (
                      <>
                        {(selectedRows || []).length === 1 ? (
                          <>
                            <Trans
                              i18nKey="contacts.menu.delete.desc"
                              values={{
                                email: selectedRows?.[0]?.work_email,
                              }}
                              components={{ bold: <b /> }}
                            />
                          </>
                        ) : (
                          `${t('contacts.menu.delete.delete_multi_desc', {
                            count: (selectedRows || []).length,
                          })}`
                        )}
                      </>
                    ),
                    glyph: 'info',
                  })
                }
              >
                {t('delete')}
              </Button>
            )}
            {/* {(blockCount > 0 || isAllSelected) && (
              <Button
                type="secondary"
                inline
                onClick={() =>
                  setConfirmationData({
                    id: new Date().toISOString(),
                    title: 'Block',
                    confirmButtonText: 'Block',
                    onConfirm: () => {
                      setConfirmationData((old) => ({
                        ...old,
                        confirmButtonDisabled: true,
                      }));
                      bulkUpdateSelectedContacts({
                        fields: {
                          is_blocked: true,
                        },
                      });
                    },
                    className: 'error',
                    description: isAllSelected
                      ? 'Do you want to block the selected contacts'
                      : `Do you want to block ${blockCount} contact${
                          blockCount > 1 ? 's' : ''
                        }?`,
                    glyph: 'info',
                  })
                }
              >
                Block
              </Button>
            )}
            {(unBlockCount > 0 || isAllSelected) && (
              <Button
                type="secondary"
                inline
                onClick={() =>
                  setConfirmationData({
                    id: new Date().toISOString(),
                    title: 'Unlock',
                    confirmButtonText: 'Unlock',
                    onConfirm: () => {
                      setConfirmationData((old) => ({
                        ...old,
                        confirmButtonDisabled: true,
                      }));
                      bulkUpdateSelectedContacts({
                        fields: {
                          is_blocked: false,
                        },
                      });
                    },
                    className: 'error',
                    description: isAllSelected
                      ? 'Do you want to unblock the selected contacts'
                      : `Do you want to unblock ${unBlockCount} contact${
                          unBlockCount > 1 ? 's' : ''
                        }?`,
                    glyph: 'info',
                  })
                }
              >
                Unblock
              </Button>
            )} */}
            {(deactivateCount > 0 || isAllSelected) && (
              <Button
                type="secondary"
                inline
                onClick={() =>
                  setConfirmationData({
                    id: new Date().toISOString(),
                    title: `${t('contacts.menu.deactivate.text')}`,
                    confirmButtonText: `${t('contacts.menu.deactivate.text')}`,
                    onConfirm: () => {
                      setConfirmationData((old) => ({
                        ...old,
                        confirmButtonDisabled: true,
                      }));
                      bulkUpdateSelectedContacts({
                        fields: {
                          contact_status: 'IN_ACTIVE',
                        },
                      });
                    },
                    className: 'error',
                    description: isAllSelected
                      ? `${t('contacts.menu.deactivate_particular_action')}`
                      : deactivateCount > 1
                      ? `${t('contacts.menu.deactivate_selected_contacts', {
                          count: deactivateCount,
                        })}`
                      : `${t('contacts.menu.deactivate_selected_contact')}`,
                    glyph: 'info',
                  })
                }
              >
                {t('contacts.menu.deactivate.text')}
              </Button>
            )}
            {(activateCount > 0 || isAllSelected) && (
              <Button
                type="secondary"
                inline
                onClick={() =>
                  setConfirmationData({
                    id: new Date().toISOString(),
                    title: 'Activate',
                    confirmButtonText: 'Activate',
                    onConfirm: () => {
                      setConfirmationData((old) => ({
                        ...old,
                        confirmButtonDisabled: true,
                      }));
                      bulkUpdateSelectedContacts({
                        fields: {
                          contact_status: 'ACTIVE',
                        },
                      });
                    },
                    className: 'error',
                    description: isAllSelected
                      ? `${t('contacts.menu.activate_particular_action')}`
                      : activateCount > 1
                      ? `${t('contacts.menu.activate_selected_contacts', {
                          count: activateCount,
                        })}`
                      : `${t('contacts.menu.activate_selected_contact')}`,
                    glyph: 'info',
                  })
                }
              >
                {t('contacts.menu.activate.text')}
              </Button>
            )}
            {(unSubscribeCount > 0 || isAllSelected) && (
              <Button
                type="secondary"
                inline
                onClick={() =>
                  setConfirmationData({
                    id: new Date().toISOString(),
                    title: 'Unsubscribe',
                    confirmButtonText: 'Unsubscribe',
                    onConfirm: () => {
                      setConfirmationData((old) => ({
                        ...old,
                        confirmButtonDisabled: true,
                      }));
                      bulkUpdateSelectedContacts({
                        fields: {
                          is_unsubscribed: true,
                          unsubscribe_time: new Date().toISOString(),
                        },
                      });
                    },
                    className: 'error',
                    description: isAllSelected
                      ? `${t('contacts.menu.unsubscribe_particular_action')}`
                      : unSubscribeCount > 1
                      ? `${t('contacts.menu.unsubscribe_selected_contacts', {
                          count: unSubscribeCount,
                        })}`
                      : `${t('contacts.menu.unsubscribe_selected_contact')}`,
                    glyph: 'info',
                  })
                }
              >
                {t('contacts.menu.unsubscribe.text')}
              </Button>
            )}
            {(subscribeCount > 0 || isAllSelected) && (
              <Button
                type="secondary"
                inline
                onClick={() =>
                  setConfirmationData({
                    id: new Date().toISOString(),
                    title: 'Subscribe',
                    confirmButtonText: 'Subscribe',
                    onConfirm: () => {
                      setConfirmationData((old) => ({
                        ...old,
                        confirmButtonDisabled: true,
                      }));
                      bulkUpdateSelectedContacts({
                        fields: {
                          is_unsubscribed: false,
                          unsubscribe_time: null,
                        },
                      });
                    },
                    className: 'error',
                    description: isAllSelected
                      ? `${t('contacts.menu.subscribe_particular_action')}`
                      : subscribeCount > 1
                      ? `${t('contacts.menu.subscribe_selected_contacts', {
                          count: subscribeCount,
                        })}`
                      : `${t('contacts.menu.subscribe_selected_contact')}`,
                    glyph: 'info',
                  })
                }
              >
                {t('contacts.menu.subscribe.text')}
              </Button>
            )}
            <UpdateFieldsButton
              contactLists={contactLists}
              isLoading={isLoadingBulkpdateContacts}
              bulkUpdateSelectedContacts={bulkUpdateSelectedContacts}
            />
            <CreateNewListButton
              contactLists={contactLists}
              bulkUpdateSelectedContacts={bulkUpdateSelectedContacts}
              isAllSelected={isAllSelected}
              selectedRows={selectedRows}
              isLoading={isLoadingBulkpdateContacts}
              hideLists={hideLists}
              setConfirmationData={setConfirmationData}
              accountInfo={accountInfo}
              userInfo={userInfo}
            />
            {contactList?.display_id && (
              <RemoveFromListButton
                setConfirmationData={setConfirmationData}
                isAllSelected={isAllSelected}
                contactList={contactList}
                selectedRowsLength={selectedRows?.length || 0}
                bulkUpdateSelectedContacts={bulkUpdateSelectedContacts}
              />
            )}
          </div>
          <div className="header-actions" />
        </>
      ) : (
        <>
          <div className="table-action-buttons">
            {filterViewComponent && <>{filterViewComponent}</>}
            {!hideSearch && !showSearchInActions && (
              <SearchInput searchBy={type} />
            )}
          </div>
          <div className="header-actions">
            {!hideSearch && showSearchInActions && (
              <SearchInput searchBy={type} />
            )}
            {!hideNewButton && (
              <NewButton
                fields={fields}
                setConfirmationData={setConfirmationData}
                executeSegment={executeSegment}
                accountInfo={accountInfo}
                contactsCount={contactsCount ?? 0}
              />
            )}
            {access.indexOf('contact:write') > -1 && (
              <>
                {showNewContact && (
                  <Button
                    disabled={
                      isNil(contactsCount) ||
                      isNaN(contactsCount) ||
                      contactsCount >=
                        (Number(
                          accountInfo?.resource_limit_configuration?.contacts,
                        ) || CONTACTS_IMPORT_LIMIT)
                    }
                    id="new-contact-btn"
                    type="secondary"
                    inline
                    onClick={() =>
                      history.push(`${history.location.pathname}?open=contact`)
                    }
                  >
                    + {t('new_contact')}
                  </Button>
                )}
                {showNewList && (
                  <Button
                    id="new-list"
                    type="secondary"
                    inline
                    disabled={
                      isLoading ||
                      (contactListsCount || 0) >=
                        (Number(
                          accountInfo?.resource_limit_configuration
                            ?.contact_lists,
                        ) || CONTACT_LISTS_LIMIT)
                    }
                    onClick={() =>
                      history.push(`${history.location.pathname}?open=list`)
                    }
                  >
                    + {t('new_list')}
                  </Button>
                )}
                {showNewSegemnt && (
                  <Button
                    id="new-segment-btn"
                    type="secondary"
                    inline
                    disabled={
                      isLoading ||
                      (segmentsCount || 0) >=
                        (Number(
                          accountInfo?.resource_limit_configuration?.segments,
                        ) || SEGMENTS_LIMIT)
                    }
                    onClick={() => history.push('/segments/create')}
                  >
                    + {t('new_segment')}
                  </Button>
                )}
              </>
            )}

            {!hideEditColumns && (
              <EditColumnsButton
                fields={columns && columns.length > 0 ? columns : fields}
                columnOrder={columnOrder}
                hiddenColumns={hiddenColumns}
                columnSettings={columnSettings}
                rule={rule}
                setColumnOrder={setColumnOrder}
                setHiddenColumns={setHiddenColumns}
              />
            )}
            {!hideExportColumns &&
              access.indexOf('contact:write') > -1 &&
              access.indexOf('contact-impexp:write') > -1 && (
                <ExportColumnsButton
                  fields={columns && columns.length > 0 ? columns : fields}
                  columnOrder={columnOrder}
                  columnSettings={columnSettings}
                  rule={rule}
                  defaultFilterCondition={defaultFilterCondition}
                  filter={filter}
                  hiddenColumns={hiddenColumns}
                  filterViewCount={filterViewCount}
                />
              )}
          </div>
        </>
      )}
    </ComponentContainer>
  );
};

export default HeaderComponent;

export const ComponentContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  height: 100%;
  // overflow: auto;
  .heading-text {
    font-weight: 600;
    font-size: 14px;
    line-height: 17px;
    color: #12344d;
  }
  .table-action-buttons {
    display: flex;
    padding: 0 4px;
    height: 100%;
    align-items: center;
    overflow: auto;
  }
  .header-actions {
    display: flex;
    align-items: center;
    & > :not(:last-child) {
      margin-right: 8px;
    }
  }
`;
